import './polyfills/buffer'; // must run before any blog/gray-matter module evaluates
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { BillingProvider } from './contexts/BillingContext';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { installGlobalErrorHandlers } from './logging/installGlobalErrorHandlers';
// Metric-matched fallback faces — bundled (same-origin, render-blocking with the
// rest of the app CSS) so first paint is laid out correctly while the real
// Google fonts load asynchronously (see index.html). Kills the font-swap CLS.
import './fonts-fallback.css';
import './index.css';

// Analytics is consent-gated — <CookieConsent> initializes it only after the
// user opts in (see web/src/components/CookieConsent), so we no longer fire it
// unconditionally here.

// Catch errors that escape React's render tree (async handlers, bare
// rejections) and ship them to CloudWatch via the API's /client-logs sink.
installGlobalErrorHandlers();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <FeatureFlagsProvider>
              <BillingProvider>
                <App />
              </BillingProvider>
            </FeatureFlagsProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
